/**
 * Team Spain v0 — Gestor de Sincronización Outbox y Resiliencia Offline (M5)
 *
 * Administra el envío ordenado e idempotente de operaciones en cola:
 * - Detección activa de conectividad (sin depender ciegamente de navigator.onLine).
 * - Reintentos acotados con backoff exponencial para fallos transitorios.
 * - Clasificación inequívoca de errores: red, auth caducada, permisos revocados, conflicto y validación.
 * - Prevención de split-brain y last-write-wins ciego.
 * - Notificaciones reactivas a la interfaz sin modales intrusivos en cada snap.
 */

import { supabase } from '../supabase'
import { offlineIndexedDb } from './indexedDb'
import {
  OutboxOperation,
  SyncErrorCategory,
  ConflictRecord,
} from '../../types/offline'

export type SyncEventType =
  | 'sync_started'
  | 'sync_progress'
  | 'sync_completed'
  | 'sync_error'
  | 'auth_required'
  | 'conflict_detected'

export interface SyncEventPayload {
  type: SyncEventType
  pendingCount: number
  currentOp?: OutboxOperation
  error?: string
  errorCategory?: SyncErrorCategory
  conflict?: ConflictRecord
}

type SyncListener = (event: SyncEventPayload) => void

export class OutboxSyncManager {
  private isSyncing = false
  private listeners: Set<SyncListener> = new Set()
  private syncTimeout: ReturnType<typeof setTimeout> | null = null
  private retryDelayMs = 2000
  private maxRetries = 5

  constructor() {
    this.setupListeners()
  }

  /**
   * Suscribe a eventos de sincronización para actualizar la interfaz
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Alias de suscripción para compatibilidad de vistas
   */
  onStatusChange(listener: SyncListener): () => void {
    return this.subscribe(listener)
  }

  private emit(event: SyncEventPayload) {
    for (const listener of this.listeners) {
      try {
        listener(event)
      } catch (err) {
        console.error('Error en sync listener:', err)
      }
    }
  }

  /**
   * Configura detectores de cambio de conectividad y foco de la ventana
   */
  private setupListeners() {
    if (typeof window === 'undefined') return

    // 1. Al recuperar conectividad en el navegador
    window.addEventListener('online', () => {
      this.triggerSync('online_event')
    })

    // 2. Al volver la pestaña al primer plano
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.triggerSync('visibility_change')
      }
    })

    // 3. Al recibir foco la ventana
    window.addEventListener('focus', () => {
      this.triggerSync('window_focus')
    })
  }

  /**
   * Comprueba si existe conexión real verificando acceso a la API
   */
  async checkReachability(): Promise<boolean> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return false
    }

    if (!supabase) return false

    try {
      // Petición ligera de conectividad real con timeout de 3 segundos
      const abortController = new AbortController()
      const timeoutId = setTimeout(() => abortController.abort(), 3500)

      const { error } = await supabase
        .from('teams')
        .select('id')
        .limit(1)
        .abortSignal(abortController.signal)

      clearTimeout(timeoutId)
      return !error || error.code !== 'PGRST301' // reachable
    } catch {
      return false
    }
  }

  /**
   * Dispara una ronda de sincronización si no hay otra en curso
   */
  async triggerSync(triggerSource: string = 'manual', teamId?: string, userId?: string): Promise<{
    syncedCount: number
    failedCount: number
    conflictsCount: number
  }> {
    void triggerSource
    if (this.isSyncing) {
      return { syncedCount: 0, failedCount: 0, conflictsCount: 0 }
    }

    this.isSyncing = true
    let syncedCount = 0
    let failedCount = 0
    let conflictsCount = 0

    try {
      // 1. Verificar si hay operaciones pendientes
      const ops = await offlineIndexedDb.getPendingOutboxOperations(teamId, userId)
      if (ops.length === 0) {
        this.emit({ type: 'sync_completed', pendingCount: 0 })
        return { syncedCount: 0, failedCount: 0, conflictsCount: 0 }
      }

      this.emit({ type: 'sync_started', pendingCount: ops.length })

      // 2. Verificar conectividad real
      const isReachable = await this.checkReachability()
      if (!isReachable) {
        this.emit({
          type: 'sync_error',
          pendingCount: ops.length,
          error: 'Sin conexión real con el servidor de Team Spain.',
          errorCategory: 'network',
        })
        return { syncedCount: 0, failedCount: ops.length, conflictsCount: 0 }
      }

      // 3. Procesar operaciones en orden secuencial estricto
      for (let i = 0; i < ops.length; i++) {
        const op = ops[i]
        const remainingCount = ops.length - i

        this.emit({
          type: 'sync_progress',
          pendingCount: remainingCount,
          currentOp: op,
        })

        // Si la operación está en conflicto o superó los reintentos máximos, no ciclar infinito
        if (op.status === 'conflict') {
          conflictsCount++
          continue
        }
        if (op.attempts >= this.maxRetries && op.error_category !== 'network') {
          failedCount++
          continue
        }

        await offlineIndexedDb.updateOutboxOperationStatus(op.operation_id, 'syncing')

        try {
          const result = await this.executeOperation(op)

          if (result.success) {
            syncedCount++
            // Eliminar de la outbox tras confirmación inequívoca
            await offlineIndexedDb.removeOutboxOperation(op.operation_id)

            // Si es un snap, marcarlo como synced en local_snaps
            if (op.entity_type === 'snap') {
              await offlineIndexedDb.markSnapSynced(op.entity_id, result.serverRevision)
            }
          } else if (result.category === 'conflict') {
            conflictsCount++
            await offlineIndexedDb.updateOutboxOperationStatus(
              op.operation_id,
              'conflict',
              result.error,
              'conflict'
            )
            this.emit({
              type: 'conflict_detected',
              pendingCount: remainingCount,
              currentOp: op,
              error: result.error,
              conflict: {
                operation_id: op.operation_id,
                entity_id: op.entity_id,
                local_payload: op.payload,
                server_payload: result.serverData,
                message: result.error || 'Conflicto de edición concurrente detectado.',
                created_at: new Date().toISOString(),
              },
            })
            // Los conflictos requieren resolución explícita; pausar operaciones dependientes
            break
          } else if (result.category === 'auth') {
            await offlineIndexedDb.updateOutboxOperationStatus(
              op.operation_id,
              'pending',
              result.error,
              'auth'
            )
            this.emit({
              type: 'auth_required',
              pendingCount: remainingCount,
              currentOp: op,
              error: 'Tu sesión ha caducado. Vuelve a iniciar sesión para enviar tus jugadas guardadas.',
            })
            break
          } else if (result.category === 'permission') {
            failedCount++
            await offlineIndexedDb.updateOutboxOperationStatus(
              op.operation_id,
              'failed',
              result.error,
              'permission'
            )
            this.emit({
              type: 'sync_error',
              pendingCount: remainingCount,
              currentOp: op,
              error: 'Permiso revocado en el servidor. Las jugadas se conservan localmente y pueden exportarse de emergencia.',
              errorCategory: 'permission',
            })
            break
          } else {
            // Fallo transitorio de red u otro
            failedCount++
            await offlineIndexedDb.updateOutboxOperationStatus(
              op.operation_id,
              'pending',
              result.error,
              result.category || 'network'
            )
            // Programar reintento con backoff exponencial
            this.scheduleRetry(op.attempts)
            break
          }
        } catch (execErr: unknown) {
          failedCount++
          const errorMsg = execErr instanceof Error ? execErr.message : 'Error de sincronización'
          await offlineIndexedDb.updateOutboxOperationStatus(
            op.operation_id,
            'pending',
            errorMsg,
            'network'
          )
          this.scheduleRetry(op.attempts)
          break
        }
      }

      const finalPending = await offlineIndexedDb.countPendingOperations(undefined, teamId, userId)
      this.emit({
        type: 'sync_completed',
        pendingCount: finalPending,
      })

      return { syncedCount, failedCount, conflictsCount }
    } finally {
      this.isSyncing = false
    }
  }

  /**
   * Ejecuta una operación individual contra las RPCs de Supabase
   */
  private async executeOperation(op: OutboxOperation): Promise<{
    success: boolean
    serverRevision?: number
    error?: string
    category?: SyncErrorCategory
    serverData?: any
  }> {
    if (!supabase) {
      return { success: false, error: 'Supabase no inicializado', category: 'network' }
    }

    try {
      switch (op.operation_type) {
        case 'commit_snap': {
          const { snap, participants, events, annotations } = op.payload
          const { data, error } = await supabase.rpc('commit_field_snap', {
            p_snap: snap,
            p_participants: participants,
            p_events: events || [],
            p_annotations: annotations || [],
          })

          if (error) {
            return this.classifySupabaseError(error)
          }
          return { success: true, serverRevision: data?.revision }
        }

        case 'correct_snap_result': {
          const { snapId, newPrimaryResult, passerAthleteId, receiverAthleteId, tacklerAthleteId } =
            op.payload
          const { data, error } = await supabase.rpc('correct_field_snap_result', {
            p_snap_id: snapId,
            p_new_primary_result: newPrimaryResult,
            p_passer_athlete_id: passerAthleteId || null,
            p_receiver_athlete_id: receiverAthleteId || null,
            p_tackler_athlete_id: tacklerAthleteId || null,
          })

          if (error) {
            return this.classifySupabaseError(error)
          }
          return { success: true, serverRevision: data?.revision }
        }

        case 'update_snap_details': {
          const { snapId, patch, events } = op.payload
          const { data, error } = await supabase.rpc('update_field_snap_details', {
            p_snap_id: snapId,
            p_patch: patch,
            p_events: events ?? null,
          })

          if (error) {
            return this.classifySupabaseError(error)
          }
          return { success: true, serverRevision: data?.revision }
        }

        case 'void_snap': {
          const { snapId, validity, reason } = op.payload
          const { error } = await supabase
            .from('snaps')
            .update({
              validity: validity || 'void',
              review_notes: reason ? `[Anulado]: ${reason}` : '[Anulado]',
              status: 'finalized',
              updated_at: new Date().toISOString(),
            })
            .eq('id', snapId)

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'finalize_capture': {
          const { captureId, notes } = op.payload
          const { error } = await supabase.rpc('finalize_capture', {
            p_capture_id: captureId,
            p_notes: notes || null,
          })

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'reopen_capture': {
          const { captureId, reason } = op.payload
          const { error } = await supabase.rpc('reopen_capture', {
            p_capture_id: captureId,
            p_reason: reason || null,
          })

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'update_match_context': {
          const { captureId, matchContext } = op.payload
          const { error } = await supabase
            .from('team_block_captures')
            .update({
              match_context: matchContext,
              updated_at: new Date().toISOString(),
            })
            .eq('id', captureId)

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'update_active_lineups': {
          const {
            captureId,
            offenseLineupName,
            offenseSlots,
            offenseIsCustom,
            defenseLineupName,
            defenseSlots,
            defenseIsCustom,
          } = op.payload

          const { error } = await supabase
            .from('team_block_captures')
            .update({
              offense_lineup_name: offenseLineupName,
              offense_slots: offenseSlots,
              offense_lineup_is_custom: Boolean(offenseIsCustom),
              defense_lineup_name: defenseLineupName,
              defense_slots: defenseSlots,
              defense_lineup_is_custom: Boolean(defenseIsCustom),
              updated_at: new Date().toISOString(),
            })
            .eq('id', captureId)

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'update_series_score': {
          const { captureId, showSeriesScore, offenseScore, defenseScore } = op.payload
          const patch: Record<string, unknown> = { updated_at: new Date().toISOString() }
          if (showSeriesScore !== undefined) patch.show_series_score = showSeriesScore
          if (offenseScore !== undefined) patch.series_score_offense = offenseScore
          if (defenseScore !== undefined) patch.series_score_defense = defenseScore

          const { error } = await supabase
            .from('team_block_captures')
            .update(patch)
            .eq('id', captureId)

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        case 'handover_coordinator': {
          const { captureId, newDeviceId, newUserId } = op.payload
          const { error } = await supabase.rpc('handover_coordinator', {
            p_capture_id: captureId,
            p_new_device_id: newDeviceId,
            p_new_user_id: newUserId,
          })

          if (error) return this.classifySupabaseError(error)
          return { success: true }
        }

        default:
          return {
            success: false,
            error: `Operación no soportada: ${op.operation_type}`,
            category: 'validation',
          }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido de ejecución'
      return { success: false, error: msg, category: 'network' }
    }
  }

  /**
   * Clasifica errores devueltos por PostgREST / RPC de Supabase
   */
  private classifySupabaseError(error: { code?: string; message: string; details?: string }): {
    success: false
    error: string
    category: SyncErrorCategory
    serverData?: any
  } {
    const code = error.code || ''
    const msg = error.message || ''

    // P0002 = Conflicto de concurrencia optimista en RPC
    if (code === 'P0002' || msg.includes('Conflicto de concurrencia') || code === '409') {
      return {
        success: false,
        error: `Conflicto de versión en el servidor: ${msg}`,
        category: 'conflict',
      }
    }

    // P0003 = No autorizado (rol coach requerido)
    // 42501 = RLS violation
    if (code === 'P0003' || code === '42501' || msg.includes('No autorizado') || code === '403') {
      return {
        success: false,
        error: `Permiso revocado o denegado: ${msg}`,
        category: 'permission',
      }
    }

    // Error de autenticación / JWT caducado
    if (code === 'PGRST301' || msg.includes('JWT') || msg.includes('token') || code === '401') {
      return {
        success: false,
        error: 'Sesión caducada o no válida.',
        category: 'auth',
      }
    }

    // Error de validación de negocio (P0001)
    if (code === 'P0001' || code === '23514') {
      return {
        success: false,
        error: `Validación de datos: ${msg}`,
        category: 'validation',
      }
    }

    // Fallo transitorio / de red
    return {
      success: false,
      error: msg,
      category: 'network',
    }
  }

  /**
   * Programa reintento con backoff exponencial
   */
  private scheduleRetry(attemptNumber: number) {
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout)
    }

    // Backoff: 2s, 4s, 8s, 16s, hasta máx 30s
    const delay = Math.min(30000, this.retryDelayMs * Math.pow(2, Math.max(0, attemptNumber - 1)))
    this.syncTimeout = setTimeout(() => {
      this.triggerSync('retry_timer')
    }, delay)
  }
}

export const syncManager = new OutboxSyncManager()
